/**
 * Cabinet → Payments (Phase F)
 *
 * Customer payment history + outstanding unpaid amounts. Also surfaces
 * SmartPaw's bank details (from admin settings) so users know where to pay.
 */
import React, { useEffect, useState } from 'react';
import { Wallet, AlertCircle, Loader2, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/cabinet/lib/api';
import { useI18n } from '@/cabinet/i18n';
import PageHeader from '@/cabinet/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

function fmtDate(iso, lang) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(lang === 'ka' ? 'ka-GE' : 'en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

export default function PaymentsPage() {
  const { t, lang } = useI18n();
  const [loading, setLoading] = useState(true);
  const [paid, setPaid] = useState([]);
  const [unpaid, setUnpaid] = useState([]);
  const [bank, setBank] = useState(null);

  useEffect(() => { document.title = 'SmartPaw — Payments'; }, []);

  useEffect(() => {
    (async () => {
      try {
        const [p, b] = await Promise.all([
          api.get('/cabinet/payments'),
          api.get('/cabinet/bank-details').catch(() => ({ data: null })),
        ]);
        setPaid(p.data?.paid || []);
        setUnpaid(p.data?.unpaid_delivered || []);
        setBank(b.data || null);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="payments-page">
      <PageHeader title={t('payments.title')} subtitle={t('payments.subtitle')} testIdPrefix="payments" />

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-32 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Outstanding */}
          {unpaid.length > 0 && (
            <Card className="border-rose-200 bg-rose-50/40" data-testid="payments-unpaid">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-rose-800 font-bold mb-2">
                  <AlertCircle size={16} /> {t('payments.unpaid_title')}
                </div>
                <ul className="space-y-2">
                  {unpaid.map((d) => (
                    <li key={d.id} className="flex items-center justify-between text-sm" data-testid={`unpaid-row-${d.id}`}>
                      <div>
                        <strong className="text-[hsl(var(--foreground))]">{d.product_name}</strong>
                        <span className="text-[hsl(var(--muted-foreground))]"> × {d.qty} · {fmtDate(d.delivered_at || d.scheduled_at, lang)}</span>
                      </div>
                      <Badge className="rounded-full bg-rose-200 text-rose-900 text-[10px]">{t('payments.unpaid_badge')}</Badge>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Bank details */}
          {bank && (bank.bank_name || bank.iban) && (
            <Card className="border-[hsl(var(--border))] bg-paper" data-testid="payments-bank-details">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-[hsl(var(--terracotta))] font-bold mb-3">
                  <Banknote size={18} /> {t('payments.bank_title')}
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {bank.bank_name && <KV k={t('payments.bank_name')} v={bank.bank_name} />}
                  {bank.account_holder && <KV k={t('payments.account_holder')} v={bank.account_holder} />}
                  {bank.iban && <KV k="IBAN" v={bank.iban} />}
                  {bank.swift && <KV k="SWIFT" v={bank.swift} />}
                  {bank.bank_code && <KV k={t('payments.bank_code')} v={bank.bank_code} />}
                </dl>
              </CardContent>
            </Card>
          )}

          {/* History */}
          <Card className="border-[hsl(var(--border))] bg-paper" data-testid="payments-history">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-[hsl(var(--foreground))] font-bold mb-3">
                <Wallet size={18} /> {t('payments.history_title')}
              </div>
              {paid.length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('payments.history_empty')}</p>
              ) : (
                <ul className="divide-y divide-[hsl(var(--border))]">
                  {paid.map((d) => (
                    <li key={d.id} className="flex items-center justify-between py-2.5 text-sm" data-testid={`paid-row-${d.id}`}>
                      <div>
                        <strong className="text-[hsl(var(--foreground))]">{d.product_name}</strong>
                        <span className="text-[hsl(var(--muted-foreground))]"> × {d.qty}</span>
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">{fmtDate(d.paid_at, lang)} · {t('payments.reference_label')}: <code className="text-[hsl(var(--foreground))]">{d.payment_reference || '—'}</code></div>
                      </div>
                      <Badge className="rounded-full bg-emerald-100 text-emerald-800 text-[10px]">{t('payments.paid_badge')}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-3 border-b border-[hsl(var(--border))]/40 py-1 last:border-0">
      <dt className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{k}</dt>
      <dd className="font-medium text-[hsl(var(--foreground))] text-right">{v}</dd>
    </div>
  );
}
